import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { authAPI } from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [resetUrl, setResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetUrl('');
    
    try {
      const res = await authAPI.forgotPassword(email);
      const data = res.data;
      setSubmitted(true);
      if (data.reset_url && !data.email_sent) {
        setResetUrl(data.reset_url);
      }
    } catch (err) {
      console.error('Password reset request failed:', err);
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_virtual-admin-1/artifacts/zwdakbm3_New%20Logo%20HD.png" 
              alt="Nimble Assistants" 
              className="h-16"
            />
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium">
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1">
            <Link 
              to="/login" 
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </Link>
            
            {!submitted ? (
              <>
                <CardTitle className="text-2xl font-bold">Forgot Password?</CardTitle>
                <CardDescription>
                  Enter your email address and we'll send you a link to reset your password.
                </CardDescription>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 p-4 rounded-full">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-center">
                  {resetUrl ? 'Password Reset Link' : 'Check Your Email'}
                </CardTitle>
                <CardDescription className="text-center">
                  {resetUrl ? (
                    <>The email service is currently unavailable. You can use the link below to reset your password directly.</>
                  ) : (
                    <>We've sent a password reset link to <strong>{email}</strong></>
                  )}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {submitted && resetUrl && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">Copy and share this link:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={resetUrl}
                    className="flex-1 text-xs bg-white border rounded px-2 py-1.5 text-gray-700"
                    onClick={(e) => e.target.select()}
                    data-testid="reset-url-display"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(resetUrl); }}
                    data-testid="copy-reset-url-btn"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10"
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#5FD068] hover:bg-[#4ec057] text-white font-medium py-6 rounded-lg"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Didn't receive the email? Check your spam folder or
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSubmitted(false)}
                  data-testid="resend-email-btn"
                >
                  Try another email address
                </Button>
                <div className="text-center pt-4">
                  <Link 
                    to="/login" 
                    className="text-sm text-blue-500 hover:text-blue-600 font-medium"
                  >
                    Return to login
                  </Link>
                </div>
              </div>
            )}

            {!submitted && (
              <div className="pt-6 border-t border-gray-200 mt-6 text-center">
                <p className="text-sm text-gray-700">
                  Remember your password?{' '}
                  <Link
                    to="/login"
                    className="text-blue-500 hover:text-blue-600 font-medium"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
